import { appDataDir } from "@tauri-apps/api/path";
import { seuficRoot } from "./driveLayout";

/** Where storage providers resolve paths against — a connected drive, or the
 * local Tauri app-data directory when none is (or in browser dev). */
export type DriveRootSource = { kind: "drive"; mountPoint: string } | { kind: "local" };

let activeRoot: DriveRootSource = { kind: "local" };
let cachedLocalRoot: string | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * The single seam every storage provider resolves paths through — it never
 * branches on drive-vs-local itself; it just asks for a path and gets one.
 * Switching roots (drive connects/disconnects) notifies subscribers so
 * things like the SQLite connection can be re-opened against the new root.
 */
export function setActiveDriveRoot(source: DriveRootSource): void {
  activeRoot = source;
  listeners.forEach((l) => l());
}

export function getActiveDriveRootSource(): DriveRootSource {
  return activeRoot;
}

export function onActiveRootChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function localRoot(): Promise<string> {
  cachedLocalRoot ??= (await appDataDir()).replace(/[\\/]+$/, "");
  return cachedLocalRoot;
}

/** The current root's absolute path — a drive's `SEUFIC/` folder, or the
 * local app-data directory. */
export async function getActiveRoot(): Promise<string> {
  if (activeRoot.kind === "drive") return seuficRoot(activeRoot.mountPoint);
  return localRoot();
}

/** Resolves an absolute path for a layout-relative segment (e.g.
 * `"Storage/database/seufic.db"`, `"Knowledge"`) against whichever root is
 * currently active. */
export async function resolvePath(segment: string): Promise<string> {
  const root = await getActiveRoot();
  return segment ? `${root}/${segment}` : root;
}
